import React, { useState, useEffect } from 'react';
import {
  Phone,
  MessageCircle,
  MapPin,
  Shield,
  Menu,
  X,
  Lock,
  Sparkles,
  ShieldCheck,
  Zap,
  BookOpen,
  HelpCircle,
  Calendar,
  ChevronRight,
  Clock,
  Layers,
  Crown,
  Heart
} from 'lucide-react';
import { CLINIC_CONFIG, createWhatsAppLink } from '../utils/whatsapp';
import { ClinicLogo } from './ClinicLogo';
import { SpecialistAvatar } from './SpecialistAvatar';
import { useSite } from '../context/SiteContext';
import { SiteTarget } from '../types';
import { shouldShowSiteSwitcher } from '../utils/siteConfig';

interface NavbarProps {
  onOpenLeadModal: (title?: string) => void;
  onNavigateToBlog?: () => void;
  onOpenAdminModal: () => void;
  onNavigateHome?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  onOpenLeadModal,
  onNavigateToBlog,
  onOpenAdminModal,
  onNavigateHome,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hideAdmin, setHideAdmin] = useState(true);

  const { siteKey, setSiteKey, isApollon, isFemale, isErkek } = useSite();

  useEffect(() => {
    fetch('/api/admin-security-config')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.success) {
          setHideAdmin(data.hideAdminFromPublic !== false);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (onNavigateHome) {
      onNavigateHome();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navLinks = isApollon
    ? [
        {
          name: 'Hizmetlerimiz',
          shortName: 'Hizmetler',
          href: '#hizmetler',
          icon: Sparkles,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('hizmetler')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
        {
          name: 'Botoks & Dolgu',
          shortName: 'Botoks & Dolgu',
          href: '#hizmetler',
          icon: Crown,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('hizmetler')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
        {
          name: 'Medikal Cilt Bakımı & Hydrafacial',
          shortName: 'Cilt Bakımı',
          href: '#hizmetler',
          icon: Zap,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('hizmetler')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
        {
          name: 'Lazer Epilasyon Klinikleri',
          shortName: 'Lazer Klinikleri',
          href: '#lazer-klinikleri',
          icon: ShieldCheck,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('lazer-klinikleri')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
        {
          name: 'Blog & Rehber',
          shortName: 'Blog',
          href: '#blog',
          icon: BookOpen,
          onClick: (e: React.MouseEvent) => {
            if (onNavigateToBlog) {
              e.preventDefault();
              onNavigateToBlog();
            }
          },
        },
        {
          name: 'Sık Sorulan Sorular',
          shortName: 'SSS',
          href: '#sss',
          icon: HelpCircle,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('sss')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
        {
          name: 'Klinik İletişim & Konum',
          shortName: 'İletişim & Konum',
          href: '#iletisim',
          icon: MapPin,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('iletisim')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
      ]
    : [
        {
          name: 'Uygulama Bölgeleri',
          shortName: 'Bölgeler',
          href: '#bolgeler',
          icon: Sparkles,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('bolgeler')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
        {
          name: 'Randevu & Bölge Planı',
          shortName: 'Fiyat & Plan',
          href: '#fiyat-hesapla',
          icon: Calendar,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('fiyat-hesapla')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
        {
          name: 'Doktor Güvencesi',
          shortName: 'Doktor Güvencesi',
          href: '#doktor-guvencesi',
          icon: ShieldCheck,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('doktor-guvencesi')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
        {
          name: 'Buz Lazer Teknolojisi',
          shortName: 'Buz Lazer',
          href: '#teknoloji',
          icon: Zap,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('teknoloji')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
        {
          name: 'Blog & Rehber',
          shortName: 'Blog',
          href: '#blog',
          icon: BookOpen,
          onClick: (e: React.MouseEvent) => {
            if (onNavigateToBlog) {
              e.preventDefault();
              onNavigateToBlog();
            }
          },
        },
        {
          name: 'Sık Sorulan Sorular',
          shortName: 'SSS',
          href: '#sss',
          icon: HelpCircle,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('sss')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
        {
          name: 'Klinik İletişim & Konum',
          shortName: 'İletişim & Konum',
          href: '#iletisim',
          icon: MapPin,
          onClick: () => {
            if (onNavigateHome) onNavigateHome();
            setTimeout(() => {
              document.getElementById('iletisim')?.scrollIntoView({ behavior: 'smooth' });
            }, 50);
          },
        },
      ];

  const handleSwitchSite = (key: SiteTarget) => {
    setSiteKey(key);
    setMobileMenuOpen(false);
    if (onNavigateHome) onNavigateHome();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* Top micro announcement bar - Clean aesthetic with integrated Site Switcher */}
      <div id="top-announcement-bar" className="bg-slate-100/90 border-b border-slate-200 text-xs text-slate-700 py-1.5 px-3.5 sm:px-8 lg:px-12 xl:px-16 overflow-hidden relative z-40">
        <div className="max-w-[1680px] mx-auto flex justify-between items-center gap-2">
          {/* Left: Location & Site Selector */}
          <div className="flex items-center gap-1.5 sm:gap-2.5 min-w-0">
            <span className="inline-flex items-center gap-1 text-amber-700 font-bold whitespace-nowrap text-[11px] sm:text-xs">
              <MapPin className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <span>Bursa Nilüfer FSM Bulvarı</span>
            </span>
            <span className="hidden md:inline text-slate-300">|</span>
            
            {/* Quick Multi-Site Switcher in Top Bar (Dev / Preview Only - Hidden on Production Server) */}
            {shouldShowSiteSwitcher() && (
              <div className="hidden sm:inline-flex items-center gap-1 bg-white px-1.5 py-0.5 rounded-lg border border-slate-200 text-[10px]">
                <span className="text-slate-600 font-semibold px-1">Portal:</span>
                <button
                  type="button"
                  onClick={() => handleSwitchSite('apollon')}
                  className={`px-1.5 py-0.5 rounded font-bold cursor-pointer transition-colors ${
                    siteKey === 'apollon'
                      ? 'bg-amber-600 text-white shadow-2xs'
                      : 'text-slate-600 hover:text-amber-800'
                  }`}
                  title="Apollon Güzellik & Medikal Estetik Ana Portal"
                >
                  Apollon Güzellik
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchSite('erkek')}
                  className={`px-1.5 py-0.5 rounded font-bold cursor-pointer transition-colors ${
                    siteKey === 'erkek'
                      ? 'bg-slate-900 text-amber-400 shadow-2xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Bursa Erkek Lazer Epilasyon Kliniği"
                >
                  Erkek Lazer
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchSite('kadin')}
                  className={`px-1.5 py-0.5 rounded font-bold cursor-pointer transition-colors ${
                    siteKey === 'kadin'
                      ? 'bg-rose-600 text-white shadow-2xs'
                      : 'text-slate-600 hover:text-rose-700'
                  }`}
                  title="Bursa Kadın Lazer Epilasyon Kliniği"
                >
                  Kadın Lazer
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {!hideAdmin && (
              <button
                onClick={onOpenAdminModal}
                aria-label="Admin Yönetici Paneli Girişi"
                className="hidden sm:flex text-[10px] sm:text-[11px] text-slate-700 hover:text-amber-800 font-bold items-center gap-1 bg-white px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg border border-slate-300 shadow-2xs hover:bg-slate-50 transition-colors cursor-pointer"
                title="Kriptolu Yönetici & Lead Paneli Girişi"
              >
                <Lock className="w-3 h-3 text-amber-600" />
                <span>Admin Girişi</span>
              </button>
            )}

            <span className="text-amber-800 font-semibold hidden lg:inline text-[11px] bg-amber-100/70 px-2 py-0.5 rounded-md border border-amber-200">
              Yalnızca Randevu İle
            </span>
            <a
              id="top-call-link"
              href={`tel:${CLINIC_CONFIG.landlineRaw}`}
              aria-label={`Kliniği Telefonla Arayın: ${CLINIC_CONFIG.landlineDisplay}`}
              className="text-slate-800 hover:text-amber-700 flex items-center gap-1 font-bold text-[11px] sm:text-xs transition-colors whitespace-nowrap"
            >
              <Phone className="w-3 h-3 text-amber-600 shrink-0" />
              <span>{CLINIC_CONFIG.landlineDisplay}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header Sticky Bar */}
      <header
        id="main-header"
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? 'bg-white/98 backdrop-blur-md border-b border-slate-200/90 shadow-sm py-2 sm:py-2.5'
            : 'bg-white/95 backdrop-blur-sm border-b border-slate-200/70 py-2 sm:py-3'
        }`}
      >
        <div className="max-w-[1680px] mx-auto px-3.5 sm:px-8 lg:px-12 xl:px-16 flex items-center justify-between gap-2 sm:gap-6">
          {/* Logo & Branding */}
          <a
            id="brand-logo-link"
            href="/"
            onClick={handleLogoClick}
            aria-label={`${isApollon ? 'Apollon Güzellik Merkezi' : isFemale ? 'Bursa Kadın Lazer Epilasyon' : 'Bursa Erkek Lazer Epilasyon'} Ana Sayfası`}
            className="group flex items-center shrink min-w-0 cursor-pointer"
          >
            <ClinicLogo variant="full" size="md" />
          </a>

          {/* Right Action Area (CTAs + Hamburger Menu Toggle for all screens) */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {/* Quick Action Quote CTA (Desktop) */}
            <button
              id="nav-quote-btn"
              onClick={() => onOpenLeadModal(isApollon ? 'Ücretsiz Cilt & Vücut Analizi' : 'Ücretsiz Kıl Analizi ve Fiyat Teklifi')}
              aria-label={isApollon ? 'Ücretsiz Cilt & Vücut Analizi İste' : 'Ücretsiz Kıl Analizi ve Fiyat Teklifi İste'}
              className="hidden md:inline-flex items-center justify-center h-10 px-4 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-xl hover:border-slate-400 transition-all shadow-2xs whitespace-nowrap cursor-pointer shrink-0"
            >
              {isApollon ? 'Cilt Analizi İste' : 'Kıl Analizi İste'}
            </button>

            {/* Primary Live Chat CTA (Sleek Midnight Slate & Gold Accents) */}
            <button
              type="button"
              id="nav-livechat-cta"
              onClick={() => {
                if (typeof window !== 'undefined' && window.openLiveChat) {
                  window.openLiveChat();
                } else if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('open-live-chat'));
                }
              }}
              aria-label="Uzmanımız ile Canlı Destek üzerinden görüşün"
              className="inline-flex items-center justify-center gap-1.5 h-9 sm:h-10 px-2 sm:px-4 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 border border-slate-700/80 hover:border-amber-500/40 rounded-xl shadow-sm shadow-slate-900/15 transition-all hover:scale-[1.02] shrink-0 whitespace-nowrap cursor-pointer group"
              title="Uzmanımız Şu An Çevrim İçi - Hemen Canlı Destek Başlatın"
            >
              <div className="relative shrink-0 flex items-center justify-center">
                <SpecialistAvatar size="xs" showOnlineStatus={false} bordered={false} className="shrink-0" />
                <span className="sm:hidden absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 ring-1 ring-slate-900 animate-pulse" />
              </div>

              {/* Text for Tablet & Desktop (>= 640px) */}
              <div className="hidden sm:flex flex-col text-left leading-tight shrink-0 whitespace-nowrap">
                <span className="whitespace-nowrap font-bold text-xs text-white group-hover:text-amber-300 transition-colors">Uzmanımız</span>
                <span className="text-[9px] font-semibold text-slate-300 flex items-center gap-1 whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                  Çevrim İçi • Canlı Destek
                </span>
              </div>
            </button>

            {/* Hamburger Menu Toggle Button */}
            <button
              id="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`h-9 w-9 sm:h-10 sm:w-auto sm:px-3 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 border transition-all cursor-pointer shrink-0 ${
                mobileMenuOpen
                  ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200 hover:border-slate-300'
              }`}
              aria-label={mobileMenuOpen ? "Menüyü kapat" : "Menüyü aç"}
            >
              {mobileMenuOpen ? <X className="w-4 h-4 shrink-0" /> : <Menu className="w-4 h-4 shrink-0" />}
              <span className="hidden sm:inline text-xs font-black">Menü</span>
            </button>
          </div>
        </div>

        {/* Seamless Inline Collapsible Dropdown Menu */}
        {mobileMenuOpen && (
          <div
            id="header-nav-dropdown"
            className="border-t border-slate-200/90 mt-2 bg-slate-50/98 backdrop-blur-md shadow-2xl transition-all duration-200 animate-in fade-in slide-in-from-top-2 max-h-[calc(100dvh-70px)] overflow-y-auto overscroll-contain"
          >
            <div className="max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 xl:px-16 py-4 space-y-4">
              {/* Portal Selector in Mobile Menu (Dev / Preview Only - Hidden on Production Server) */}
              {shouldShowSiteSwitcher() && (
                <div className="p-3 bg-white rounded-2xl border border-slate-200 shadow-2xs">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-amber-600" />
                    <span>Klinik & Portal Seçimi</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => handleSwitchSite('apollon')}
                      className={`p-2 rounded-xl text-center flex flex-col items-center gap-1 text-xs font-bold transition-all border cursor-pointer ${
                        siteKey === 'apollon'
                          ? 'bg-gradient-to-br from-amber-500 to-amber-700 text-white border-amber-600 shadow-xs'
                          : 'bg-slate-50 hover:bg-amber-50 text-slate-700 border-slate-200'
                      }`}
                    >
                      <Crown className="w-4 h-4" />
                      <span className="text-[11px] leading-tight">Apollon Güzellik</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSwitchSite('erkek')}
                      className={`p-2 rounded-xl text-center flex flex-col items-center gap-1 text-xs font-bold transition-all border cursor-pointer ${
                        siteKey === 'erkek'
                          ? 'bg-slate-900 text-amber-400 border-slate-800 shadow-xs'
                          : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                      }`}
                    >
                      <Zap className="w-4 h-4" />
                      <span className="text-[11px] leading-tight">Erkek Lazer</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSwitchSite('kadin')}
                      className={`p-2 rounded-xl text-center flex flex-col items-center gap-1 text-xs font-bold transition-all border cursor-pointer ${
                        siteKey === 'kadin'
                          ? 'bg-rose-600 text-white border-rose-500 shadow-xs'
                          : 'bg-slate-50 hover:bg-rose-50 text-slate-700 border-slate-200'
                      }`}
                    >
                      <Heart className="w-4 h-4" />
                      <span className="text-[11px] leading-tight">Kadın Lazer</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Navigation Links Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={link.name}
                      href={link.href}
                      onClick={(e) => {
                        if (link.onClick) link.onClick(e as any);
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center justify-between p-3 rounded-xl bg-white hover:bg-amber-50 text-slate-800 hover:text-amber-800 border border-slate-200 hover:border-amber-300 transition-all group shadow-2xs min-w-0"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-amber-100 text-slate-600 group-hover:text-amber-700 flex items-center justify-center transition-colors shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="text-xs sm:text-sm font-bold truncate">{link.name}</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-amber-600 transition-transform group-hover:translate-x-0.5 shrink-0" />
                    </a>
                  );
                })}
              </div>

              {/* Bottom Inline Actions Bar */}
              <div className="pt-3 border-t border-slate-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
                <div className="grid grid-cols-1 sm:flex sm:items-center gap-2 w-full sm:w-auto">
                  <a
                    href={`tel:${CLINIC_CONFIG.landlineRaw}`}
                    className="py-2.5 px-3.5 rounded-xl bg-white hover:bg-slate-100 text-slate-800 font-extrabold text-xs border border-slate-300 flex items-center justify-center gap-2 transition-colors shadow-2xs"
                  >
                    <Phone className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>{CLINIC_CONFIG.landlineDisplay}</span>
                  </a>

                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onOpenLeadModal(isApollon ? 'Menü Ücretsiz Cilt Danışmanlığı' : 'Menü Ücretsiz Ön Görüşme');
                    }}
                    className="py-2.5 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs flex items-center justify-center gap-2 transition-colors shadow-2xs cursor-pointer"
                  >
                    <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
                    <span>{isApollon ? 'Ücretsiz Danışmanlık Al' : 'Ücretsiz Ön Görüşme Al'}</span>
                  </button>
                </div>

                {!hideAdmin && (
                  <div className="flex items-center w-full sm:w-auto">
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        onOpenAdminModal();
                      }}
                      className="w-full sm:w-auto py-2.5 px-3.5 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 bg-white hover:bg-slate-100 border border-slate-200 flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                    >
                      <Lock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                      <span>Admin & Lead Paneli</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};
